import React, { useState, useEffect } from 'react';
import { supabase } from './supabase';
import { useTheme } from './ThemeContext';
import Login from './Login';
import VoiceChat from './VoiceChat';
import SearchPage from './SearchPage';
import Settings from './Settings';

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;

function App() {
  const { theme } = useTheme();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentPage, setCurrentPage] = useState('voice');
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    console.log('App useEffect - checking Supabase configuration...');

    if (!process.env.REACT_APP_SUPABASE_URL || !process.env.REACT_APP_SUPABASE_ANON_KEY) {
      console.error('Supabase environment variables not set!');
      setError('Supabase configuration missing. Please check environment variables.');
      setLoading(false);
      return;
    }

    let isInitialized = false;

    // For multi-tab support: use a shorter, more aggressive timeout
    const loadingTimeout = setTimeout(() => {
      if (!isInitialized) {
        console.warn('Session check timeout - checking localStorage directly');
        // Try to read session directly from localStorage as fallback for multi-tab issues
        try {
          const storageKey = 'syntari-auth';
          const storedAuth = window.localStorage.getItem(`sb-${supabaseUrl.split('//')[1].split('.')[0]}-auth-token`);
          if (storedAuth) {
            console.log('Found session in localStorage, proceeding');
          }
        } catch (e) {
          console.error('Error reading localStorage:', e);
        }
        isInitialized = true;
        setLoading(false);
      }
    }, 3000); // Reduced to 3 seconds for better UX

    // First, set up the auth state listener BEFORE calling getSession
    // This ensures we catch any auth events that fire during initialization
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      console.log('Auth state changed:', event, session?.user ? 'with user' : 'no user');

      // Handle initial session or any auth state change
      if (!isInitialized && (event === 'INITIAL_SESSION' || event === 'SIGNED_IN')) {
        isInitialized = true;
        clearTimeout(loadingTimeout);
        setLoading(false);
      }

      setUser(session?.user ?? null);

      if (event === 'SIGNED_IN' && session?.user) {
        try {
          const { data: existingUser, error: fetchError } = await supabase
            .from('users')
            .select('id')
            .eq('id', session.user.id)
            .single();

          if (fetchError && fetchError.code === 'PGRST116') {
            // User doesn't exist in database - this is a new sign-up
            console.log('New user detected via Google OAuth - blocking');

            // Sign them out immediately
            await supabase.auth.signOut();

            // Clear session
            setUser(null);
            setError('New sign-ups with Google are disabled. Please use email sign-up with an invite code.');

            return;
          }
        } catch (error) {
          console.error('Error handling user creation:', error);
        }
      }
    });

    // Then call getSession - this allows onAuthStateChange to handle it if it fires first
    supabase.auth.getSession().then(({ data: { session }, error }) => {
      if (!isInitialized) {
        isInitialized = true;
        clearTimeout(loadingTimeout);
        if (error) {
          console.error('Error getting session:', error);
          setError('Authentication error: ' + error.message);
        } else {
          console.log('Session retrieved:', session ? 'User logged in' : 'No session');
          setUser(session?.user ?? null);
        }
        setLoading(false);
      }
    }).catch((error) => {
      if (!isInitialized) {
        isInitialized = true;
        clearTimeout(loadingTimeout);
        console.error('Error in getSession:', error);
        setError('Failed to initialize authentication');
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const handleSignOut = async () => {
    console.log('Signing out...');

    // Clear local storage immediately
    const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
    const storageKey = `sb-${supabaseUrl.split('//')[1].split('.')[0]}-auth-token`;
    localStorage.removeItem(storageKey);
    localStorage.clear(); // Clear all localStorage to be safe

    // Try to sign out from Supabase with timeout
    const signOutPromise = supabase.auth.signOut();
    const timeoutPromise = new Promise((resolve) => setTimeout(resolve, 1000));

    try {
      await Promise.race([signOutPromise, timeoutPromise]);
    } catch (error) {
      console.error('Sign out error (ignored):', error);
    }

    console.log('Reloading page...');
    // Force reload regardless of Supabase response
    window.location.href = window.location.origin;
  };

  const handleNavClick = (page) => {
    setCurrentPage(page);
    setMenuOpen(false);
  };

  const styles = getStyles(theme);

  if (loading) {
    return (
      <div style={styles.loadingContainer}>
        <div style={styles.loadingSpinner}></div>
        <p style={styles.loadingText}>Loading...</p>
      </div>
    );
  }

  if (error && error.includes('Supabase configuration missing')) {
    return (
      <div style={styles.errorContainer}>
        <div style={styles.errorBox}>
          <h1 style={styles.errorTitle}>Configuration Error</h1>
          <p style={styles.errorText}>{error}</p>
          <div style={styles.configHelp}>
            <h3 style={{color: theme.text}}>To fix this issue:</h3>
            <ol style={styles.configSteps}>
              <li>Create a <code>.env.local</code> file in the frontend directory</li>
              <li>Add your Supabase credentials:</li>
            </ol>
            <pre style={styles.configCode}>
{`REACT_APP_SUPABASE_URL=your_supabase_url
REACT_APP_SUPABASE_ANON_KEY=your_supabase_anon_key
REACT_APP_BACKEND_URL=http://localhost:8000`}
            </pre>
            <p style={{color: theme.textSecondary}}>Then restart the development server with <code>npm start</code>.</p>
          </div>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Login />;
  }

  return (
    <div style={styles.mainContainer}>
      <div style={styles.header}>
        <div style={styles.logoContainer}>
          <div style={styles.logo}>S</div>
          <div>
            <h1 style={styles.title}>Syntari</h1>
          </div>
        </div>
        <button
          onClick={() => setMenuOpen(!menuOpen)}
          style={styles.hamburger}
          aria-label="Menu"
        >
          <div style={{...styles.hamburgerLine, transform: menuOpen ? 'rotate(45deg) translateY(8px)' : 'none'}}></div>
          <div style={{...styles.hamburgerLine, opacity: menuOpen ? 0 : 1}}></div>
          <div style={{...styles.hamburgerLine, transform: menuOpen ? 'rotate(-45deg) translateY(-8px)' : 'none'}}></div>
        </button>
      </div>

      {menuOpen && (
        <>
          <div style={styles.overlay} onClick={() => setMenuOpen(false)}></div>
          <div style={styles.menu}>
            <button
              onClick={() => handleNavClick('voice')}
              style={{
                ...styles.menuItem,
                backgroundColor: currentPage === 'voice' ? 'rgba(0, 212, 106, 0.1)' : 'transparent',
                color: currentPage === 'voice' ? theme.primary : theme.text,
                borderLeft: currentPage === 'voice' ? `3px solid ${theme.primary}` : '3px solid transparent',
              }}
            >
              Voice Chat
            </button>
            <button
              onClick={() => handleNavClick('search')}
              style={{
                ...styles.menuItem,
                backgroundColor: currentPage === 'search' ? 'rgba(0, 212, 106, 0.1)' : 'transparent',
                color: currentPage === 'search' ? theme.primary : theme.text,
                borderLeft: currentPage === 'search' ? `3px solid ${theme.primary}` : '3px solid transparent',
              }}
            >
              Search
            </button>
            <button
              onClick={() => handleNavClick('settings')}
              style={{
                ...styles.menuItem,
                backgroundColor: currentPage === 'settings' ? 'rgba(0, 212, 106, 0.1)' : 'transparent',
                color: currentPage === 'settings' ? theme.primary : theme.text,
                borderLeft: currentPage === 'settings' ? `3px solid ${theme.primary}` : '3px solid transparent',
              }}
            >
              Settings
            </button>
            <div style={styles.menuDivider}></div>
            <button onClick={handleSignOut} style={styles.menuItemDanger}>
              Sign Out
            </button>
          </div>
        </>
      )}

      <div style={styles.content}>
        {currentPage === 'voice' && <VoiceChat userId={user.id} />}
        {currentPage === 'search' && <SearchPage userId={user.id} />}
        {currentPage === 'settings' && (
          <Settings
            user={user}
            onSignOut={handleSignOut}
            onBack={() => setCurrentPage('voice')}
          />
        )}
      </div>
    </div>
  );
}

const getStyles = (theme) => ({
  mainContainer: {
    minHeight: '100vh',
    backgroundColor: theme.background,
    color: theme.text,
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
  },
  header: {
    position: 'sticky',
    top: 0,
    left: 0,
    right: 0,
    backgroundColor: theme.background,
    borderBottom: `1px solid ${theme.border}`,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 16px',
    zIndex: 1000,
    backdropFilter: 'blur(10px)',
  },
  logoContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
  },
  logo: {
    width: '36px',
    height: '36px',
    background: `linear-gradient(135deg, ${theme.primary} 0%, ${theme.primaryDark} 100%)`,
    borderRadius: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '18px',
    fontWeight: '800',
    color: theme.background,
    boxShadow: `0 4px 16px rgba(0, 212, 106, 0.25)`,
  },
  title: {
    fontSize: '20px',
    fontWeight: '800',
    margin: 0,
    color: theme.text,
    letterSpacing: '-0.5px',
  },
  hamburger: {
    background: 'transparent',
    border: 'none',
    padding: '6px',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    zIndex: 1001,
  },
  hamburgerLine: {
    width: '22px',
    height: '2px',
    backgroundColor: theme.primary,
    borderRadius: '2px',
    transition: 'all 0.3s ease',
  },
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    zIndex: 999,
    animation: 'fadeIn 0.3s ease',
  },
  menu: {
    position: 'fixed',
    top: '61px',
    right: '16px',
    backgroundColor: theme.surface,
    borderRadius: '14px',
    border: `1px solid ${theme.border}`,
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.6)',
    zIndex: 1001,
    minWidth: '180px',
    padding: '6px',
    animation: 'slideIn 0.3s ease',
  },
  menuItem: {
    width: '100%',
    background: 'transparent',
    border: 'none',
    padding: '12px 14px',
    fontSize: '14px',
    color: theme.text,
    cursor: 'pointer',
    borderRadius: '10px',
    display: 'flex',
    alignItems: 'center',
    transition: 'background-color 0.2s, color 0.2s',
    fontWeight: '500',
    textAlign: 'left',
  },
  menuItemDanger: {
    width: '100%',
    background: 'transparent',
    border: 'none',
    borderLeft: '3px solid transparent',
    padding: '12px 14px',
    fontSize: '14px',
    color: theme.error,
    cursor: 'pointer',
    borderRadius: '10px',
    display: 'flex',
    alignItems: 'center',
    transition: 'background-color 0.2s',
    fontWeight: '500',
    textAlign: 'left',
  },
  menuDivider: {
    height: '1px',
    backgroundColor: theme.border,
    margin: '6px 0',
  },
  content: {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'flex-start',
  },
  loadingContainer: {
    minHeight: '100vh',
    backgroundColor: theme.background,
    color: theme.text,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  loadingSpinner: {
    width: '50px',
    height: '50px',
    border: `4px solid ${theme.border}`,
    borderTop: `4px solid ${theme.primary}`,
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    marginBottom: '20px',
  },
  loadingText: {
    color: theme.textMuted,
    fontSize: '16px',
  },
  errorContainer: {
    minHeight: '100vh',
    backgroundColor: theme.background,
    color: theme.text,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '20px',
  },
  errorBox: {
    backgroundColor: theme.surface,
    padding: '40px',
    borderRadius: '20px',
    maxWidth: '600px',
    width: '100%',
    border: `1px solid ${theme.border}`,
  },
  errorTitle: {
    fontSize: '24px',
    marginBottom: '16px',
    color: theme.error,
  },
  errorText: {
    color: theme.error,
    fontSize: '16px',
    marginBottom: '20px',
  },
  configHelp: {
    marginTop: '20px',
    textAlign: 'left',
  },
  configSteps: {
    color: theme.textTertiary,
    lineHeight: '1.8',
    paddingLeft: '20px',
  },
  configCode: {
    backgroundColor: theme.background,
    padding: '15px',
    borderRadius: '12px',
    color: theme.primary,
    fontSize: '13px',
    overflow: 'auto',
    margin: '10px 0',
    border: `1px solid ${theme.border}`,
    fontFamily: 'monospace',
  },
});

export default App;
